import Checkbox from 'core-system/Checkbox'
import PrimaryButton from 'core-system/Button/PrimaryButton'
import SecondaryButton from 'core-system/Button/SecondaryButton'
import FlexContainer from 'core-system/FlexContainer'
import Loading from 'core-system/Loading'
import Text from 'core-system/Text'
import pxToRem from 'core-system/utils/pxToRem'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { tagsActions } from 'redux/tags/tagsSlice'
import styled from 'styled-components'

const Container = styled(FlexContainer)`
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`

const ContentWrapper = styled(FlexContainer)`
  flex-direction: column;
  padding-bottom: 0.5rem;
  width: 100%;
`

const CheckBoxes = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(${(props) => props.theme.pxToRem(187)}, 1fr)
  );
  grid-gap: 1rem;
  margin-top: 1rem;
`

interface MyCommuterManageTagsProps {
  employeeId: string
  onCancel: () => void
  onSaveComplete: () => void
}

const MyCommuterManageTags = React.memo((props: MyCommuterManageTagsProps) => {
  const { employeeId, onCancel, onSaveComplete } = props

  const dispatch = useDispatch()
  const { allTags, tagEmployeesUpdateSeq } = useSelector(
    (state: AppState) => state.tags
  )
  const expectedUpdatesRef = useRef(0)
  const saveStartSeqRef = useRef<number | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  const initialTagIds = useMemo(() => {
    if (!allTags) {
      return []
    }
    return allTags
      .filter((tag) => tag.employees.includes(employeeId))
      .map((tag) => tag.id)
  }, [allTags, employeeId])

  useEffect(() => {
    dispatch(tagsActions.getTags())
  }, [dispatch])

  useEffect(() => {
    setSelectedTagIds(initialTagIds)
  }, [initialTagIds])

  useEffect(() => {
    if (!isSaving || saveStartSeqRef.current === null) {
      return
    }

    const completedUpdates = tagEmployeesUpdateSeq - saveStartSeqRef.current
    if (completedUpdates < expectedUpdatesRef.current) {
      return
    }

    saveStartSeqRef.current = null
    dispatch(tagsActions.getTags())
    setIsSaving(false)
    onSaveComplete()
  }, [
    dispatch,
    isSaving,
    onSaveComplete,
    tagEmployeesUpdateSeq,
  ])

  const handleCheckboxChange = (tagId: string) => {
    setSelectedTagIds((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId)
      }
      return [...prev, tagId]
    })
  }

  const handleSave = () => {
    const tagsToAdd = selectedTagIds.filter((id) => !initialTagIds.includes(id))
    const tagsToRemove = initialTagIds.filter(
      (id) => !selectedTagIds.includes(id)
    )
    const totalUpdates = tagsToAdd.length + tagsToRemove.length

    if (totalUpdates === 0) {
      onCancel()
      return
    }

    expectedUpdatesRef.current = totalUpdates
    saveStartSeqRef.current = tagEmployeesUpdateSeq
    setIsSaving(true)

    tagsToAdd.forEach((tagId) => {
      dispatch(
        tagsActions.addTagEmployees({
          tagId,
          employees: [employeeId],
        })
      )
    })

    tagsToRemove.forEach((tagId) => {
      dispatch(
        tagsActions.removeTagEmployees({
          tagId,
          employees: [employeeId],
        })
      )
    })
  }

  const hasChanges =
    selectedTagIds.length !== initialTagIds.length ||
    selectedTagIds.some((id) => !initialTagIds.includes(id))

  if (!allTags) {
    return <Loading height={pxToRem(300)} isCard={false} />
  }

  return (
    <>
      <Container>
        <ContentWrapper>
          <Text variant='h5' marginBottom='0.5rem'>
            Manage Tags
          </Text>
          <Text variant='action4'>
            Select tags to assign to this commuter. Tags can also be managed from
            the Tags tab.
          </Text>
          {allTags.length === 0 ? (
            <Text variant='action3' marginTop='1.5rem'>
              No tags available. Create tags from the Tags tab first.
            </Text>
          ) : (
            <CheckBoxes>
              {allTags.map((tag) => (
                <Checkbox
                  key={tag.id}
                  checked={selectedTagIds.includes(tag.id)}
                  onClick={() => handleCheckboxChange(tag.id)}
                  label={tag.name}
                  marginRight='1rem'
                  disabled={isSaving}
                />
              ))}
            </CheckBoxes>
          )}
        </ContentWrapper>
      </Container>
      <FlexContainer justifyContent='flex-end' marginTop='2rem'>
        <SecondaryButton
          onClick={onCancel}
          style={{ marginRight: '1rem' }}
          disabled={isSaving}
        >
          Cancel
        </SecondaryButton>
        <PrimaryButton
          onClick={handleSave}
          disabled={isSaving || allTags.length === 0 || !hasChanges}
        >
          Save Tags
        </PrimaryButton>
      </FlexContainer>
    </>
  )
})

if (process.env.NODE_ENV !== 'production') {
  MyCommuterManageTags.displayName = 'MyCommuterManageTags'
}

export default MyCommuterManageTags
